import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal, Radio, Select, Table, Tag, Tooltip } from 'antd';
import { InfoCircleOutlined, LockOutlined, WarningOutlined } from '@ant-design/icons';
import { Dayjs } from 'dayjs';
import { apiToken } from '../../config/ApiService';
import { CopyRate, CopyResource, CopySource, copyResourceRows, matchingRate } from '../services/copyResources';
import BasePriceVersionWarning, { BasePriceVersionWarningProps } from './BasePriceVersionWarning';

export interface ResourceImportProps extends BasePriceVersionWarningProps {
    dealId?: number; start: Dayjs; end: Dayjs; duration: number; rates: CopyRate[]; firstKey: number;
    onClose: () => void;
    onApply: (data: ReturnType<typeof copyResourceRows>, replace: boolean) => void;
}

interface SourceVersion {
    version_id: number; version_name: string; ode_status: boolean; updated_on: string;
}
type SourceChoice = number | 'openair';

export default function CopyDealResourcesModal({ dealId, start, end, duration, rates, firstKey, onClose, onApply, basePriceVersionId, pricingVersions }: ResourceImportProps) {
    const [deals, setDeals] = useState<{ deal_id: number; deal_name: string; is_version_locked: boolean }[]>([]);
    const [versions, setVersions] = useState<SourceVersion[]>([]);
    const [selectedVersion, setSelectedVersion] = useState<SourceChoice>();
    const [openair, setOpenair] = useState<CopySource>();
    const [sourceWarning, setSourceWarning] = useState('');
    const [sourceId, setSourceId] = useState<number>();
    const [source, setSource] = useState<CopySource>();
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [replace, setReplace] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const roleFields = [
        ['resource_type', 'Type'], ['region', 'Region'], ['service_line', 'Service line'],
        ['function', 'Function'], ['career_level', 'Level'],
    ] as const;
    const resourceIssues = (row: CopyResource) => {
        const missing = roleFields.filter(([field]) => !row[field]?.trim()).map(([, label]) => label);
        const start = Date.parse(row.start_date), end = Date.parse(row.end_date);
        return [...(row.issues ?? []),
            ...(missing.length ? [`Missing: ${missing.join(', ')}.`] : []),
            ...((row.start_date && !Number.isFinite(start)) || (row.end_date && !Number.isFinite(end)) || start > end ? ['Source dates are invalid.'] : []),
            ...(row.allocation != null && (!Number.isFinite(row.allocation) || row.allocation < 0 || row.allocation > 100) ? ['Allocation must be between 0% and 100%.'] : []),
            ...(matchingRate(row, rates) < 0 ? ['No matching rate combination in the current pricing catalog.'] : [])];
    };
    const available = (row: CopyResource) => resourceIssues(row).length === 0;
    const eligibleKeys = (data?: CopySource) => data?.resources.flatMap((row, index) => available(row) ? [index] : []) ?? [];
    const selectedKeys = selectedRowKeys.filter(key => source?.resources[Number(key)] && available(source.resources[Number(key)]));
    const blockedCount = source?.resources.filter(row => !available(row)).length ?? 0;
    const request = useRef(0);
    const showError = (e: any) => setError(e?.response?.data?.detail || e.message || 'Unable to load resources. Please retry.');
    useEffect(() => {
        let active = true;
        apiToken.get('/pricing_tool/pricing_deals').then(response => {
            if (active) setDeals(response.data.filter((d: { deal_id: number }) => d.deal_id !== dealId));
        }).catch(e => { if (active) showError(e); });
        return () => { active = false; request.current++; };
    }, [dealId]);
    const load = async (id: number, versionId?: SourceChoice) => {
        const current = ++request.current;
        setSourceId(id); setSource(undefined); setSelectedRowKeys([]); setError(''); setLoading(true);
        if (versionId === undefined) { setVersions([]); setSelectedVersion(undefined); setOpenair(undefined); setSourceWarning(''); }
        try {
            let bookings = openair;
            if (versionId === undefined) {
                const [versionResult, bookingResult] = await Promise.allSettled([
                    apiToken.get<SourceVersion[]>('/pricing_tool/pricing_deal_versions', { params: { deal_id: id } }),
                    apiToken.get<CopySource>('/pricing_tool/copy_resources', { params: { deal_id: id, source: 'openair' } }),
                ]);
                if (current !== request.current) return;
                const data = versionResult.status === 'fulfilled' ? versionResult.value.data : [];
                bookings = bookingResult.status === 'fulfilled' ? bookingResult.value.data : undefined;
                setOpenair(bookings);
                setSourceWarning([
                    versionResult.status === 'rejected' ? 'Pricing versions could not be loaded.' : '',
                    bookingResult.status === 'rejected' ? 'OpenAir bookings could not be loaded.' : '',
                ].filter(Boolean).join(' '));
                const sorted = [...data].sort((a, b) => Date.parse(b.updated_on) - Date.parse(a.updated_on) || b.version_id - a.version_id);
                setVersions(sorted);
                const locked = deals.find(deal => deal.deal_id === id)?.is_version_locked;
                versionId = bookings?.booking_count ? 'openair' :
                    (locked ? sorted.find(version => version.ode_status) : undefined)?.version_id ?? sorted[0]?.version_id;
                if (versionId === undefined) throw new Error('No OpenAir bookings or active pricing versions found.');
            }
            setSelectedVersion(versionId);
            if (versionId === 'openair') {
                setSource(bookings);
                setSelectedRowKeys(eligibleKeys(bookings));
                return;
            }
            const { data } = await apiToken.get<CopySource>('/pricing_tool/copy_resources', {
                params: { deal_id: id, version_id: versionId }
            });
            if (current !== request.current) return;
            setSource(data);
            setSelectedRowKeys(eligibleKeys(data));
        } catch (e) { if (current === request.current) showError(e); }
        finally { if (current === request.current) setLoading(false); }
    };
    const apply = () => {
        if (!source || loading) return;
        try {
            const rows = source.resources.filter((_, index) => selectedKeys.includes(index));
            if (!rows.length || rows.some(row => !available(row))) return;
            onApply(copyResourceRows(rows, rates, source.project_start_date, start, end, duration, firstKey), replace);
            onClose();
        } catch (e) { showError(e); }
    };
    const finalVersionId = deals.find(d => d.deal_id === sourceId)?.is_version_locked
        ? versions.find(v => v.ode_status)?.version_id : undefined;
    const sourceLabel = (value: unknown, label: React.ReactNode) => <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        {value === finalVersionId && finalVersionId !== undefined && <Tag color="success" icon={<LockOutlined />}
            title="Locked final pricing version" aria-label="Locked final pricing version"
            style={{ marginInlineEnd: 0, flexShrink: 0 }}>Final</Tag>}
    </span>;
    return <Modal open title={<>Copy resources from deal<BasePriceVersionWarning basePriceVersionId={basePriceVersionId} pricingVersions={pricingVersions} /></>} width={1150} onCancel={onClose} onOk={apply}
        okText={replace ? 'Replace resources' : 'Add resources'} okButtonProps={{ disabled: loading || !source?.resources.length || !selectedKeys.length }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
        <label htmlFor="copy-source-deal" style={{ flex: '0 0 92px', fontWeight: 600 }}>Source deal</label>
        <Select id="copy-source-deal" showSearch optionFilterProp="label" placeholder="Search source deal" style={{ flex: 1, minWidth: 0 }}
            value={sourceId} onChange={id => load(id)} options={deals.map(d => ({ value: d.deal_id, label: `${d.deal_name} (${d.deal_id})` }))} />
        </div>
        {(versions.length > 0 || !!openair?.booking_count) && <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
            <label htmlFor="copy-source-version" style={{ flex: '0 0 92px', fontWeight: 600 }}>Copy from</label>
            <Select<SourceChoice> id="copy-source-version" aria-label="Copy from" style={{ flex: 1, minWidth: 0 }}
            value={selectedVersion} onChange={id => load(sourceId!, id)}
            optionRender={option => sourceLabel(option.value, option.label)}
            labelRender={option => sourceLabel(option.value, option.label)}
            options={[
                ...(openair?.booking_count ? [{ value: 'openair' as const, label: 'OpenAir bookings' }] : []),
                ...versions.map(v => ({ value: v.version_id, label: v.version_name })),
            ]} /></div>}
        {sourceWarning && <Alert type="warning" message={sourceWarning}
            action={<Button size="small" disabled={loading} onClick={() => load(sourceId!)}>Retry sources</Button>} />}
        {!!source?.skipped_booking_count && <Alert type="warning"
            message={`${source.skipped_booking_count} OpenAir booking(s) skipped because their role, employee type, dates, or allocation could not be mapped.`} />}
        {!!blockedCount && <Alert type="warning"
            style={{ marginTop: 10 }} message={`${blockedCount} resource(s) cannot be selected. See row details.`} />}
        {source && !source.resources.length && <Alert type="warning" message="No resources found in this source." />}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: 14, marginBottom: 8 }}>
            <span style={{ fontWeight: 500 }}>Resources to copy{source && !loading ? ` · ${selectedKeys.length} of ${source.resources.length} selected` : ''}</span>
            {selectedVersion === 'openair' && source && !loading && <span style={{ color: '#666', fontSize: 12 }}>· {source.booking_count ?? 0} source booking{source.booking_count === 1 ? '' : 's'}</span>}
            <Tooltip trigger={['hover', 'focus', 'click']} title={<>
                <div>Pricing versions retain proposed prices; OpenAir uses catalog rates. Dates and allocations adjust to this project.</div>
                {selectedVersion === 'openair' && <div style={{ marginTop: 8 }}>Bookings with the same employee and role are merged using earliest start, latest end, and average allocation, even when role mapping fails. Missing employee IDs or missing/invalid dates or allocations keep bookings separate.</div>}
                <div style={{ marginTop: 8 }}>— means missing data. Unavailable rows cannot be selected; correct the source details or choose a base price version with a matching rate.</div>
            </>}>
                <Button type="text" size="small" aria-label="About copying resources" icon={<InfoCircleOutlined />} />
            </Tooltip>
        </div>
        <Table size="small" loading={loading} pagination={false} scroll={{ y: 320, x: 1050 }}
            rowKey="key" dataSource={source?.resources.map((r, key) => ({ ...r, key })) ?? []}
            rowSelection={{ selectedRowKeys: selectedKeys, onChange: setSelectedRowKeys,
                getCheckboxProps: row => ({ disabled: !available(row), name: `resource-${row.key}`, 'aria-label': `Select resource ${row.key + 1}: ${row.name || 'Not specified'}` }) }}
            columns={[
                { title: 'Preferred Resource', dataIndex: 'name', width: 240, className: 'title-column', align: 'left',
                    render: (value, row) => <>{value || '—'}{!!row.booking_ids?.length && <div style={{ color: '#666', fontSize: 12 }}>Booking IDs: {row.booking_ids.join(', ')}</div>}</> },
                { title: 'Resource', className: 'title-column', align: 'left',
                    width: 220, render: (_, row) => <div style={{ lineHeight: 1.45 }}>
                        <div style={{ fontWeight: 500 }}>{row.function || '—'} · {row.career_level || '—'}</div>
                        <div style={{ color: '#666', fontSize: 12 }}>{row.resource_type || '—'} · {row.region || '—'}</div>
                        <div style={{ color: '#666', fontSize: 12 }}>Service line: {row.service_line || '—'}</div>
                    </div> },
                { title: 'Source dates', width: 145, render: (_, row) => <>
                    <div>Start: {row.source_details?.start_date || row.start_date?.slice(0, 10) || '—'}</div>
                    <div>End: {row.source_details?.end_date || row.end_date?.slice(0, 10) || '—'}</div>
                </> },
                { title: 'Allocation', dataIndex: 'allocation', width: 90, className: 'total-column', align: 'right',
                    render: (value, row) => row.source_details?.allocation != null ? `${row.source_details.allocation}%` : value == null ? '—' : `${value}%` },
                { title: 'Review details', width: 180, render: (_, row) => {
                    const issues = resourceIssues(row);
                    return <><Tag color={issues.length ? 'warning' : 'success'}>{issues.length ? 'Unavailable' : 'Ready to copy'}</Tag>
                        {issues.map((issue, index) => <div key={index} style={{ marginTop: 4 }}>{issue}</div>)}
                        {!issues.length && (!row.start_date || !row.end_date) && <div style={{ marginTop: 4, color: '#666' }}>Missing dates: uses project dates.</div>}
                        {!issues.length && row.allocation == null && <div style={{ marginTop: 4, color: '#666' }}>Missing allocation: uses 100%.</div>}
                        {row.source_details && <div style={{ marginTop: 4, color: '#666' }}>Source employee type: {row.source_details.employee_type || '—'} · Employee ID: {row.source_details.user_id || '—'}</div>}
                    </>;
                } },
                ...(selectedVersion === 'openair' ? [{ title: 'Booking types', dataIndex: 'booking_types', width: 120,
                    className: 'title-column', align: 'left' as const,
                    render: (types?: string[]) => types?.join(', ') || '—' }] : []),
            ]} />
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px 16px', marginTop: 12 }}>
        <Radio.Group value={replace} onChange={e => setReplace(e.target.value)}>
            <Radio value={true}>Copy resources</Radio>
            <Radio value={false}>Add to existing resources</Radio>
        </Radio.Group>
        {replace && <span style={{ color: '#ad6800', fontSize: 12 }}><WarningOutlined aria-hidden="true" /> Replaces both sections, including unsaved edits.</span>}
        </div>
        {error && <Alert style={{ marginTop: 12 }} type="error" showIcon message={error}
            action={sourceId && !source ? <Button size="small" disabled={loading} onClick={() => load(sourceId, selectedVersion)}>Retry</Button> : undefined} />}
    </Modal>;
}
